class BlogFacets extends HTMLElement {
  constructor() {
    super();
    this.state = {
      fetched_blog_articles: [],
      blog_articles: [],
      current_cursor: null,
      hasNextPage: false,
    };

    this.blogCountId = '#blogCount';
    this.blogFiltersCountId = '#blogFiltersCount';
    this.blogResultsCountId = '#blogResultsCount';
    this.blogAllCountId = '#blogAllCount';
    this.blogClearAllFiltersId = '#blogClearAllFilters';
    this.blogGridLiquidContainerId = '#blogGridContainer';
    this.blogGridJsContainerId = '#blogGridJsContainer';
    this.blogGridJsId = '#blog-grid-js';
    this.facetCheckboxInputsSelector = ".facets-layout input[type='checkbox']";
    this.facetCloseButton = this.querySelector('button.mobile-facets__close-button');
    this.facetsPillsForm = this.querySelector('#FacetFiltersPillsForm .active-facets');
    this.blogTitle = this.getAttribute('data-blog-title');
    this.blogTitles = this.getAttribute('data-blog-titles');
    this.storefrontApiToken = this.getAttribute('data-storefront-api-token');
    this.cacheDays = this.getAttribute('data-cache-days');
    this.storeUrl = window.Shopify.shop;
    this.allArticlesQuery = this.getAllBlogArticlesQuery();
    this.allowedFilters = ['sport', 'location'];
    this.formattedBlogTitlesString = this.blogTitles
      ? this.blogTitles
          .split('-')
          .map((title) => `blog_title:'${title}'`)
          .join(' OR ')
      : null;
  }

  connectedCallback() {
    this.getCachedArticles();
    this.setInitialCheckboxStates();
    this.attachCheckboxListeners();
    this.attachClearAllListener();
    this.updateActiveFacetsButtons();
    this.updateActiveFiltersCount();
    this.attachFacetCloseButtonListener();

    let previousUrl = '';
    const observer = new MutationObserver(() => {
      if (window.location.href !== previousUrl) {
        previousUrl = window.location.href;
        this.filterArticles();
        this.renderArticlesNumber();
        this.hideEmptyFacetItems();
      }
    });
    const config = { subtree: true, childList: true };

    observer.observe(document, config);
  }

  attachFacetCloseButtonListener() {
    if (this.facetCloseButton) {
      this.facetCloseButton.addEventListener('click', (e) => {
        e.preventDefault();
      });
    }
  }

  getAllBlogArticlesQuery() {
    return `
      query AllBlogArticles($first: Int!, $cursor: String, $query: String) {
        articles(first: $first, after: $cursor, query: $query, sortKey: PUBLISHED_AT, reverse: true) {
          nodes {
            title
            contentHtml
            image {
              url
              altText
            }
            authorV2 {
              firstName
              lastName
            }
            handle
            publishedAt
            location: metafield(key: "location", namespace: "yonex") {
              reference {
                ... on Metaobject {
                  handle
                  title: field(key: "title") {
                    value
                  }
                }
              }
            }
            sport: metafield(key: "sport", namespace: "yonex") {
              reference {
                ... on Metaobject {
                  handle
                  sport_name: field(key: "sport_name") {
                    value
                  }
                }
              }
            }
            blog {
              handle
            }
          }
          pageInfo {
            endCursor
            hasNextPage
            hasPreviousPage
            startCursor
          }
        }
      }
    `;
  }

  async getCachedArticles() {
    const blogTitle = this.blogTitle ? this.blogTitle.toLowerCase().replace(/\s+/g, '-') : this.blogTitles;
    const cacheKey = `blog_${blogTitle}_data`;
    const cacheDays = Number(this.cacheDays);
    const cacheDuration =
      cacheDays > 0
        ? cacheDays * 24 * 60 * 60 * 1000 // X days in milliseconds
        : 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    const cachedData = JSON.parse(localStorage.getItem(cacheKey));
    const currentTime = Date.now();

    if (cachedData && cachedData.timestamp + cacheDuration > currentTime) {
      console.log('Using cached blog data');
      this.setState('fetched_blog_articles', cachedData.blog_articles);
      this.filterArticles();
      this.renderArticlesNumber();
      this.hideEmptyFacetItems();
    } else {
      console.log('Cached blog_articles data is outdated or does not exist. Fetching new data.');
      localStorage.removeItem(cacheKey); // Clear outdated data

      // fetch and display first articles on the page
      if (this.blogTitles) {
        await this.fetchFirstArticles();
      } else {
        await this.fetchAllArticles(); // Fetch new blog_articles and update cache
      }
    }
  }

  async fetchArticles(first, cursor = null) {
    const variables = {
      first,
      cursor,
      query: this.blogTitle
        ? `blog_title:'${this.blogTitle}'`
        : this.blogTitles
          ? this.formattedBlogTitlesString
          : null,
    };

    const blogArticlesQuery = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontApiToken,
      },
      body: JSON.stringify({ query: this.allArticlesQuery, variables }),
    };

    try {
      const response = await fetch(`https://${this.storeUrl}/api/2025-01/graphql`, blogArticlesQuery);
      const result = await response.json();

      if (result.errors) {
        console.error('GraphQL error:', result.errors);
        return { articles: [], hasNextPage: false, endCursor: null };
      }

      const { nodes, pageInfo } = result.data.articles;
      this.setState('hasNextPage', pageInfo?.hasNextPage || false);

      return {
        articles: nodes || [],
        endCursor: pageInfo?.endCursor || null,
      };
    } catch (error) {
      console.error('Error fetching blog_articles:', error);
      return { articles: [], hasNextPage: false, endCursor: null };
    }
  }

  async fetchFirstArticles() {
    const { articles, endCursor } = await this.fetchArticles(50);

    if (this.state.hasNextPage) {
      this.setState('current_cursor', endCursor);
    }

    const updatedArticles = articles.map((article) => ({
      ...article,
      contentHtml: this.cleanContentHtml(article.contentHtml),
    }));

    this.setState('fetched_blog_articles', updatedArticles);
    this.filterArticles();
    await this.fetchAllArticles();
  }

  async fetchAllArticles() {
    let { fetched_blog_articles, current_cursor } = this.state;

    while (this.state.hasNextPage) {
      const { articles, endCursor } = await this.fetchArticles(250, current_cursor);
      fetched_blog_articles = [...fetched_blog_articles, ...articles];
      current_cursor = endCursor;
    }

    const updatedArticles = fetched_blog_articles.map((article) => ({
      ...article,
      contentHtml: this.cleanContentHtml(article.contentHtml),
    }));

    this.setState('fetched_blog_articles', updatedArticles);
    this.setState('current_cursor', null);
    this.filterArticles();
    this.renderArticlesNumber();
    this.hideEmptyFacetItems();

    // Cache the new data with a timestamp
    const currentTime = Date.now();
    const blogTitle = this.blogTitle ? this.blogTitle.toLowerCase().replace(/\s+/g, '-') : this.blogTitles;
    localStorage.setItem(
      `blog_${blogTitle}_data`,
      JSON.stringify({
        blog_articles: updatedArticles,
        timestamp: currentTime,
      }),
    );
  }

  cleanContentHtml(html) {
    if (typeof html !== 'string' || !html.trim()) return '';

    return html
      .replace(/<style[\s\S]*?<\/style>/gi, '') // Remove <style> tags and their content
      .replace(/<script[\s\S]*?<\/script>/gi, '') // Remove <script> tags and their content
      .replace(/<\/?[^>]+>/g, '') // Remove all remaining HTML tags
      .replace(/&nbsp;/g, ' ') // Convert non-breaking spaces
      .replace(/\s+/g, ' ') // Normalize spaces and newlines
      .trim() // Remove leading/trailing whitespace
      .slice(0, 250); // Limit to 250 characters
  }

  filterArticles() {
    const urlParams = new URLSearchParams(window.location.search);
    const sports = urlParams.get('sport')?.split(',') || [];
    const locations = urlParams.get('location')?.split(',') || [];

    const filteredArticles = this.state.fetched_blog_articles?.filter((article) => {
      const articleSport = article.sport?.reference?.handle;
      const articleLocation = article.location?.reference?.handle;

      const sportMatches = sports.length === 0 || sports.includes(articleSport);
      const locationMatches = locations.length === 0 || locations.includes(articleLocation);

      return sportMatches && locationMatches;
    });

    this.setState('blog_articles', filteredArticles || []);

    this.setCorrectLayoutDisplaying();
  }

  renderArticlesNumber() {
    const { blog_articles, fetched_blog_articles } = this.state;
    if (fetched_blog_articles && fetched_blog_articles.length > 0) {
      const articlesCount = this.querySelector(`${this.blogCountId}`);
      if (articlesCount) {
        articlesCount.textContent = `${blog_articles.length}`;
      }

      const blogResultsCountId = this.querySelector(`${this.blogResultsCountId}`);
      if (blogResultsCountId) {
        blogResultsCountId.textContent = `${blog_articles.length}`;
      }

      const blogAllCountId = this.querySelector(`${this.blogAllCountId}`);
      if (blogAllCountId) {
        blogAllCountId.textContent = `${fetched_blog_articles.length}`;
      }
    }
  }

  setCorrectLayoutDisplaying() {
    const { blog_articles, fetched_blog_articles } = this.state;
    const blogGridLiquidContainerId = document.querySelector(this.blogGridLiquidContainerId);
    const blogGridJsContainer = document.querySelector(this.blogGridJsContainerId);

    if (blog_articles.length === fetched_blog_articles.length && this.blogTitle) {
      blogGridLiquidContainerId.style.display = 'block';
      blogGridJsContainer.style.display = 'none';
    } else {
      blogGridLiquidContainerId.style.display = 'none';
      blogGridJsContainer.style.display = 'block';

      this.renderCustomLayoutContent();
    }
  }

  renderCustomLayoutContent() {
    const { blog_articles, current_cursor } = this.state;
    const blogLayout = document.querySelector(this.blogGridJsId);
    const noResultsBlock = document.querySelector('.blog-grid__no-results');

    if (!blogLayout) {
      console.error('Blog layout container not found.');
      return;
    }

    if (blog_articles.length === 0) {
      if (noResultsBlock) {
        noResultsBlock.style.display = 'block';
        blogLayout.innerHTML = '';
      }
    } else {
      if (noResultsBlock) {
        noResultsBlock.style.display = 'none';
      }

      let sortedArticles = [...blog_articles].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

      if (current_cursor == null) {
        // Clear existing content before adding new items
        blogLayout.innerHTML = '';
      } else {
        const articlesTitle = blogLayout.querySelectorAll('.article-card-wrapper .card__content .card__heading');
        const lastArticleTitle = articlesTitle[articlesTitle.length - 1]?.innerHTML;
        if (lastArticleTitle) {
          const lastArticleIndex = sortedArticles.findIndex((article) => article.title === lastArticleTitle);

          // Get all articles after the found one
          sortedArticles = lastArticleIndex !== -1 ? sortedArticles.slice(lastArticleIndex + 1) : [];
        }
      }

      sortedArticles.forEach((article, index) => {
        // Format date as MM.DD.YYYY
        const formattedDate = new Date(article.publishedAt)
          .toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })
          .replace(/\//g, '.');

        // Create a new <div> element
        const blogArticle = document.createElement('div');
        blogArticle.className = 'blog-articles__article grid__item article scroll-trigger animate--slide-in';
        blogArticle.setAttribute('data-cascade', '');
        blogArticle.style.setProperty('--animation-order', index + 1);

        // Construct the image HTML only if the article has an image
        const imageHtml = article.image
          ? `<div class="article-card__image-wrapper card__media">
                <div class="article-card__image media media--hover-effect"> 
                  <img src="${article.image.url}" 
                     alt="${article.image.altText || ''}" 
                     loading="lazy">
                </div>
             </div>`
          : '';

        // Construct the inner HTML using the article data
        blogArticle.innerHTML = `
          <div class="article-card-wrapper card-wrapper">
            <div class="card article-card card--standard card--media article-card__image--medium" style="--ratio-percent: 100%;">
              <div class="card__inner color-scheme-2 gradient" style="--ratio-percent: 100%;">
                ${imageHtml}
                <div class="card__content">
                  <div class="card__information">
                    <div class="article-card__info caption-with-letter-spacing h5">
                      <p class="circle-divider">${formattedDate}</p>
                    </div>
                    <h3 class="card__heading h4">${article.title}</h3>
                    <p class="article-card__excerpt rte-width">${article.contentHtml}</p>
                    <div class="article-card__footer"></div>
                  </div>
                </div>
              </div>
              <div class="card__content">
                <div class="card__information">
                  <div class="article-card__info caption-with-letter-spacing">
                    <p class="circle-divider">${formattedDate}</p>
                  </div>
                  <h3 class="card__heading h4">${article.title}</h3>
                  <p class="article-card__excerpt rte-width">${article.contentHtml}</p>
                  <div class="article-card__footer"></div>
                  <a href="/blogs/${article.blog.handle}/${article.handle}" class="button button--secondary">
                    Read more
                  </a>
                </div>
              </div>
            </div>
          </div>
        `;

        // Append the article to the blog layout
        blogLayout.appendChild(blogArticle);
      });
    }
  }

  hideEmptyFacetItems() {
    const { fetched_blog_articles } = this.state;

    const existingLocations = new Set(
      fetched_blog_articles.map((article) => article?.location?.reference?.title?.value),
    );
    const existingSports = new Set(
      fetched_blog_articles.map((article) => article?.sport?.reference?.sport_name?.value),
    );

    // Hide individual filter items if they don't have corresponding values
    document.querySelectorAll('.mobile-facets__item[data-location]').forEach((item) => {
      const location = item.getAttribute('data-location');
      if (!existingLocations.has(location)) {
        item.style.display = 'none';
      }
    });

    document.querySelectorAll('.mobile-facets__item[data-sport]').forEach((item) => {
      const sport = item.getAttribute('data-sport');
      if (!existingSports.has(sport)) {
        item.style.display = 'none';
      }
    });

    // Hide the parent .mobile-facets__details if all children have display: none
    document.querySelectorAll('.mobile-facets__details').forEach((parent) => {
      const children = parent.querySelectorAll('.mobile-facets__item');
      const allHidden = Array.from(children).every((child) => child.style.display === 'none');

      if (allHidden) {
        parent.style.display = 'none';
      }
    });
  }

  setInitialCheckboxStates() {
    const urlParams = new URLSearchParams(window.location.search);
    this.querySelectorAll(this.facetCheckboxInputsSelector).forEach((checkbox) => {
      const key = checkbox.closest('.facets-layout').dataset.name;
      if (urlParams.has(key) && urlParams.get(key).split(',').includes(checkbox.name)) {
        checkbox.checked = true;
      }
    });
  }

  attachCheckboxListeners() {
    this.querySelectorAll(this.facetCheckboxInputsSelector).forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => this.handleCheckboxChange(e.target));
    });
  }

  handleCheckboxChange(checkbox) {
    const key = checkbox.closest('.facets-layout').dataset.name;
    const urlParams = new URLSearchParams(window.location.search);
    const values = urlParams.has(key) ? urlParams.get(key).split(',') : [];

    if (checkbox.checked) {
      values.push(checkbox.name);
    } else {
      const index = values.indexOf(checkbox.name);
      if (index > -1) values.splice(index, 1);
    }

    if (values.length > 0) {
      urlParams.set(key, values.join(','));
    } else {
      urlParams.delete(key);
    }

    history.replaceState(null, '', `?${urlParams.toString()}`);
    this.updateActiveFacetsButtons();
    this.updateActiveFiltersCount();
  }

  attachClearAllListener() {
    const clearAllBtn = this.querySelector(this.blogClearAllFiltersId);
    clearAllBtn?.addEventListener('click', () => {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.forEach((_, key) => urlParams.delete(key));
      history.replaceState(null, '', `?`);
      this.querySelectorAll(this.facetCheckboxInputsSelector).forEach((checkbox) => (checkbox.checked = false));
      this.updateActiveFacetsButtons();
      this.updateActiveFiltersCount();
    });
  }

  updateActiveFiltersCount() {
    const blogFiltersCount = this.querySelector(`${this.blogFiltersCountId}`);
    const urlParams = new URLSearchParams(window.location.search);

    let activeFilterCount = 0;

    urlParams.forEach((value, key) => {
      if (this.allowedFilters.includes(key)) {
        if (value.includes(',')) {
          activeFilterCount += value.split(',').length;
        } else {
          activeFilterCount += 1;
        }
      }
    });

    if (activeFilterCount > 0) {
      blogFiltersCount.textContent = `(${activeFilterCount})`;
    } else {
      blogFiltersCount.textContent = '';
    }
  }

  updateActiveFacetsButtons() {
    if (!this.facetsPillsForm) return;
    const urlParams = new URLSearchParams(window.location.search);

    // Clear existing buttons
    this.facetsPillsForm.innerHTML = '';

    let hasActiveFilters = false;

    // Add new buttons
    urlParams.forEach((value, key) => {
      if (this.allowedFilters.includes(key)) {
        value.split(',').forEach((filterValue) => {
          hasActiveFilters = true;
          const formattedText = this.formatFilterText(filterValue);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'active-facets__button active-facets__button--light';
          button.innerHTML = `
          <span class="active-facets__button-inner">
          <span class="svg-wrapper" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" class="icon icon-close-small" width="11.999" height="12" viewBox="0 0 11.999 12">
            <path d="M11.827,10.995l-5-5,5-4.994a.589.589,0,0,0-.832-.832L6,5.167,1.005.173a.589.589,0,0,0-.834.832L5.167,6l-5,5a.589.589,0,0,0,.834.832L6,6.832l5,5a.589.589,0,0,0,.832-.832" transform="translate(0)"></path>
          </svg></span>
            ${formattedText}
            <span class="visually-hidden">Remove filter</span>
          </span>
        `;
          button.addEventListener('click', () => this.removeFilter(key, filterValue));
          this.facetsPillsForm.appendChild(button);
        });
      }
    });

    if (hasActiveFilters) {
      this.facetsPillsForm.classList.remove('active-facets--empty');
    } else {
      this.facetsPillsForm.classList.add('active-facets--empty');
    }

    this.updateActiveFiltersCount();
  }

  formatFilterText(value) {
    // Replace hyphens with spaces, capitalize each word
    return value
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  removeFilter(key, value) {
    const urlParams = new URLSearchParams(window.location.search);
    const values = urlParams
      .get(key)
      .split(',')
      .filter((v) => v !== value);
    if (values.length > 0) {
      urlParams.set(key, values.join(','));
    } else {
      urlParams.delete(key);
    }

    history.replaceState(null, '', `?${urlParams.toString()}`);
    this.querySelectorAll(`.facets-layout input[name='${value}']`).forEach((checkbox) => (checkbox.checked = false));
    this.updateActiveFacetsButtons();
  }

  setState(key, value) {
    if (!Object.keys(this.state).includes(key)) return;
    this.state[key] = value;
    const event = new Event(`state.${this.id}.${key}`);
    document.dispatchEvent(event);
  }
}

customElements.define('blog-facets', BlogFacets);
